import { useState, useEffect } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MapPin, Loader2 } from "lucide-react";
import useScrollToTop from "@/hooks/useScrollToTop";
import MaintenanceNotice from "@/components/common/MaintenanceNotice";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { Helmet } from "react-helmet-async";
import { CONTACT_ENDPOINT } from "@/config/urls";

const Contact = () => {
  useScrollToTop();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Create a style element to fix autofill background
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
      input:-webkit-autofill,
      input:-webkit-autofill:hover, 
      input:-webkit-autofill:focus,
      textarea:-webkit-autofill,
      textarea:-webkit-autofill:hover,
      textarea:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px #000 inset !important;
        -webkit-text-fill-color: #fff !important;
        transition: background-color 5000s ease-in-out 0s;
      }
    `;
    document.head.appendChild(styleEl);
    
    // Cleanup
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      // Try to parse JSON if returned, else synthesize minimal object
      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json") ? await response.json() : { success: response.ok };

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Failed to send message");
      }

      setFormData({
        name: "",
        email: "",
        subject: "",
        message: ""
      });
      toast({
        title: "Message Sent",
        description: "We've received your message and will get back to you soon.",
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "There was a problem sending your message. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact | YouAux</title>
        <meta name="description" content="Have questions about our music distribution services? Get in touch with our team." />
        <link rel="canonical" href="https://youaux.com/contact" />
      </Helmet>
    <div className="ophira-bg min-h-screen bg-black">
      <Navbar />
      <main className="container mx-auto px-4 py-28">
        {/* <MaintenanceNotice /> */}
        
        <section className="mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white text-center">
            Contact Us
          </h1>
          <p className="text-ophira-lightgray max-w-2xl text-lg text-center mx-auto mb-12">
            Have questions about our music distribution services? Get in touch with our team.
          </p>
        </section>

        <div className="flex flex-col items-center gap-16">
          <div className="w-full max-w-2xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="border-ophira-yellow/20 bg-black focus:border-ophira-yellow/50 rounded-none py-6 px-4 [&:-webkit-autofill]:!bg-black [&:-webkit-autofill]:!text-white [&:autofill]:!bg-black"
              />
              <Input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="border-ophira-yellow/20 bg-black focus:border-ophira-yellow/50 rounded-none py-6 px-4 [&:-webkit-autofill]:!bg-black [&:-webkit-autofill]:!text-white [&:autofill]:!bg-black"
              />
              <Input
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="border-ophira-yellow/20 bg-black focus:border-ophira-yellow/50 rounded-none py-6 px-4 [&:-webkit-autofill]:!bg-black [&:-webkit-autofill]:!text-white [&:autofill]:!bg-black"
              />
              <Textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="border-ophira-yellow/20 bg-black focus:border-ophira-yellow/50 rounded-none py-4 px-4 min-h-[200px] [&:-webkit-autofill]:!bg-black [&:-webkit-autofill]:!text-white [&:autofill]:!bg-black"
              />
              <div className="flex justify-center">
                <Button 
                  type="submit" 
                  className="ophira-button w-auto px-8" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </div>
            </form>
          </div>

          <div className="bg-black/20 p-8 border-2 border-ophira-yellow/20 w-full max-w-4xl">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-white mb-4 text-xl font-bold">Address</h3>
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-ophira-yellow mt-1" />
                  <p className="text-ophira-lightgray">
                    B1/639 A, Janakpuri, West Delhi, Delhi 110058, India
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-white mb-4 text-xl font-bold">Email</h3>
                <div className="flex items-center gap-3">
                  <Mail size={20} className="text-ophira-yellow" />
                  <p className="text-ophira-lightgray">support@youaux.com</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-white mb-4 text-xl font-bold">Connect With Us</h3>
                <div className="flex gap-4">
                  <a href="https://www.linkedin.com/company/youaux-entertainment/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-ophira-yellow/10 hover:bg-ophira-yellow/20 flex items-center justify-center text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  <a href="https://x.com/YouAuxMusic" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-ophira-yellow/10 hover:bg-ophira-yellow/20 flex items-center justify-center text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
                    </svg>
                  </a>
                  <a href="https://www.instagram.com/youaux" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-ophira-yellow/10 hover:bg-ophira-yellow/20 flex items-center justify-center text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a href="https://www.facebook.com/share/1CTF2Mpsie/?mibextid=qi2Omg" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-ophira-yellow/10 hover:bg-ophira-yellow/20 flex items-center justify-center text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <Toaster />
    </div>
    </>
  );
};

export default Contact;
